import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box } from '@mui/material';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import usePrintDeliveryNoteReceived from 'api/hooks/usePrintDeliveryNoteReceived';
import theme from 'App.theme';
import Button from 'components/Button';
import Dialog from 'components/Dialog';
import { useLanguage } from 'context/AppContext';
import { useCheckInTraysBySet } from 'hooks/useCheckInTraysBySet';
import { useIsTierZero } from 'hooks/useSubscriptionTier';
import Routes, { DELIVERY_ID_PARAM } from 'routes';

type Props = {
  deliveryId: string;
  onClose: VoidFunction;
};

const ReceiveDeliverySuccessDialog = ({ deliveryId, onClose }: Props) => {
  const lang = useLanguage()[0].ReceiveDeliverySuccessDialog;

  const isTierZero = useIsTierZero();

  const navigate = useNavigate();
  const setCheckInTraysBySet = useCheckInTraysBySet()[1];

  const { fetchPdf: fetchDeliveryNoteReceived } = usePrintDeliveryNoteReceived();
  const [generatingDocument, setGeneratingDocument] = useState(false);

  const generateReceivedDocument = useCallback(async () => {
    setGeneratingDocument(true);
    try {
      const blob = await fetchDeliveryNoteReceived({ deliveryId });
      const pdfUrl = URL.createObjectURL(blob);
      window.open(pdfUrl);
    } finally {
      setGeneratingDocument(false);
    }
  }, [deliveryId, fetchDeliveryNoteReceived]);

  const checkInNow = useCallback(() => {
    // skip the usual "do you want to check in this delivery now?" confirmation dialog -
    // this button already is that confirmation
    setCheckInTraysBySet({});
    onClose();
    navigate(Routes.CheckInWorkflow.path.replace(DELIVERY_ID_PARAM, deliveryId));
  }, [deliveryId, navigate, onClose, setCheckInTraysBySet]);

  return (
    <Dialog open title={lang.title} allowClose={false}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme => theme.layout.spacing.small,
          padding: theme => theme.layout.spacing.medium,
        }}
      >
        <CheckCircleOutlineIcon
          sx={{
            width: '4em',
            height: '4em',
            color: theme => theme.colors.success,
            alignSelf: 'center',
            marginBottom: theme => theme.layout.spacing.medium,
          }}
        />
        <Box
          sx={{
            marginTop: theme.layout.spacing.medium,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.layout.spacing.medium,
          }}
        >
          <Button
            label={lang.generateReceivedDocument}
            onClick={generateReceivedDocument}
            colors="white on active"
            disabled={generatingDocument}
            loading={generatingDocument}
          />

          {!isTierZero && (
            <Button colors="white on active" onClick={checkInNow}>
              {lang.checkInNow}
            </Button>
          )}

          <Button colors="white on active" onClick={onClose}>
            {lang.close}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ReceiveDeliverySuccessDialog;
